<script setup lang="ts">
import { useProjectStore } from '@/stores/project'
import { useCalculationStore } from '@/stores/calculation'
import { storeToRefs } from 'pinia'
import { DeleteDialog } from '@haskoning/vue-component-library'
import { useAuthorizationStore } from '@/stores/authorization'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { DateTime } from 'luxon'
import type { Calculation, CalculationFormPayload } from '@/stores/type/calculation.type'
import type { Project, ProjectSpendingCapPayload } from '@/stores/type/project.type'
import CalculationTableActions from '@/components/calculation-index/CalculationTableActions.vue'
import CalculationCreateUpdateDialog from '@/components/calculation-index/CalculationCreateUpdateDialog.vue'
import ProjectSpendingSummary from '@/components/project-detail/ProjectSpendingSummary.vue'
import { useCalculationPermissions } from '@/composables/useCalculationPermissions'
import ProjectCreateUpdateDialog from '@/components/project/ProjectCreateUpdateDialog.vue'

const projectStore = useProjectStore()
const calculationStore = useCalculationStore()
const authorizationStore = useAuthorizationStore()
const router = useRouter()

const {
  currentItem: currentProjectItem,
  createUpdateDialog: projectCreateUpdateDialog,
  writeLoading: projectWriteLoading,
  fieldsFiltered: projectFieldsFiltered,
  projectFormErrors,
  spending,
  spendingLoading,
  spendingWriteLoading
} = storeToRefs(projectStore)
const {
  currentItem: currentCalculationItem,
  createUpdateDialog: calculationCreateUpdateDialog,
  fetchLoading: calculationFetchLoading,
  items: calculationItems,
  deleteDialog,
  deleteItem,
  deleteLoading,
  writeLoading: calculationWriteLoading,
  fields: calculationFields,
  zipFileDownloadLoading
} = storeToRefs(calculationStore)
const { canAddCalculation, canStopCalculation, canDeleteCalculation } = useCalculationPermissions()

const canChangeProject = computed(() => {
  return (
    !currentProjectItem.value?.is_demo &&
    authorizationStore.hasServicePermission({
      authSource: 'User',
      allowed: ['change_project']
    })
  )
})

const headers = computed(() => [
  { title: '#', key: 'id', align: 'start' as const },
  { title: 'Name', key: 'name', align: 'start' as const },
  { title: 'Status', key: 'status', align: 'start' as const },
  { title: 'Hardware Profile', key: 'hardware_profile_name', align: 'start' as const },
  { title: 'ZIP File', key: 'zip_file_name', align: 'start' as const, sortable: false },
  {
    title: 'Storage',
    key: 'estimated_monthly_storage_cost',
    align: 'start' as const
  },
  { title: 'Compute', key: 'estimated_compute_cost', align: 'start' as const },
  { title: 'Created', key: 'created', align: 'start' as const },
  { title: 'Deadline', key: 'vm_runtime_deadline', align: 'start' as const },
  { title: 'Completed', key: 'ended', align: 'start' as const },
  { title: 'Duration', key: 'duration', align: 'start' as const, sortable: false },
  ...(canAddCalculation.value || canStopCalculation.value || canDeleteCalculation.value
    ? [{ title: 'Actions', key: 'actions', align: 'center' as const, sortable: false }]
    : [])
])
const search = ref('')
const SPENDING_CAP_FIELD_PARAMETERS = ['compute_spending_cap_eur', 'storage_spending_cap_eur']

const filterItemsBySearch = <T extends Calculation>(
  items: T[],
  search: string,
  keys: (keyof T)[]
): T[] => {
  if (!search) return items
  return items.filter((item) =>
    keys.some((key) => item[key]?.toString().toLowerCase().includes(search.toLowerCase()))
  )
}

const getFilteredItems = (
  items: Calculation[],
  search: string,
  favorite: boolean
): Calculation[] => {
  const filteredItems = [...filterItemsBySearch(items, search, ['name', 'status'])]
    .sort((a, b) => sortTimestamp(b) - sortTimestamp(a))
    .sort((a, b) => Number(b.favorite) - Number(a.favorite))

  return favorite ? filteredItems.filter((item) => item.favorite) : filteredItems
}

const sortTimestamp = (item: Calculation) => {
  return new Date(item.ended ?? item.started ?? item.created ?? item.modified).getTime()
}

const filteredCalculations = computed(() =>
  getFilteredItems(calculationItems.value, search.value, false)
)

const navigateToCalculation = (_event: Event, row: { item: Calculation }) => {
  void router.push({
    name: 'calculation-result',
    params: { id: row.item.project, calculationId: row.item.id }
  })
}

const downloadZipFile = (item: Calculation) => {
  if (zipFileDownloadLoading.value === item.id) return
  calculationStore.downloadZipFile(item)
}

const getStatus = (item: Calculation): string => {
  if (item.status === 'DRAFT' && item.task_id) return 'Queued'
  if (item.status === 'DRAFT' && item.input_ready && item.input_auto_start && !item.task_id)
    return 'Starting calculation'
  if (item.status === 'DRAFT' && !item.input_ready) {
    if (item.input_upload_status === 'VALIDATING') return 'Checking input'
    if (item.input_upload_status === 'FAILED') return 'Input upload failed'
    return 'Input upload needed'
  }
  const choices = calculationFields.value?.['status']?.choices ?? []
  return choices.find((choice) => choice.value === item.status)?.display_name ?? item.status
}

const formatDate = (value?: string | null) => {
  if (!value) return '-'
  const date = DateTime.fromISO(value)
  return date.isValid ? date.toFormat('dd-MM-yy HH:mm') : '-'
}

const formatDuration = (item: Calculation) => {
  if (!item.started || !item.ended) return '-'

  const started = DateTime.fromISO(item.started)
  const ended = DateTime.fromISO(item.ended)
  if (!started.isValid || !ended.isValid || ended.toMillis() < started.toMillis()) return '-'

  const seconds = Math.floor(ended.diff(started, 'seconds').seconds)
  if (seconds < 60) return `${seconds}s`

  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return [
    days ? `${days}d` : null,
    hours ? `${hours}h` : null,
    minutes || (!days && !hours) ? `${minutes}m` : null
  ]
    .filter(Boolean)
    .join(' ')
}

const formatMoney = (item: Calculation, value?: number | null, suffix = '') => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-'
  const formatted = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: item.cost_currency ?? 'EUR',
    maximumFractionDigits: 2
  }).format(Number(value))
  return `${formatted}${suffix}`
}

const spendingLoaded = computed(() => Boolean(spending.value))
const canSetProjectSpendingCap = computed(() =>
  Boolean(currentProjectItem.value?.can_increase_spending_cap)
)

const projectDialogFields = computed(() => {
  if (canSetProjectSpendingCap.value) return projectFieldsFiltered.value
  return projectFieldsFiltered.value.filter(
    (field) => !SPENDING_CAP_FIELD_PARAMETERS.includes(field.parameter)
  )
})

const projectDialogDisabledFields = computed(() => [])
const projectWorkOrderField = computed(() =>
  projectStore.fieldsList.find((field) => field.parameter === 'work_order')
)

const handleEditProject = () => {
  projectStore.clearProjectFormErrors()
  projectStore.openCreateUpdateDialog()
}

const saveProject = async (item: Project) => {
  if (!item) return
  const savedProject = await projectStore.saveProjectForm(item)
  if (!savedProject) return

  projectStore.closeCreateUpdateDialog()
  const projectId = Number(savedProject.id)
  if (projectId) {
    void projectStore.fetchSpending(projectId)
  }
}

const addCalculation = () => {
  if (!currentProjectItem.value?.id) return
  const newCalculation = calculationStore.createNewCalculation(currentProjectItem.value.id)
  calculationStore.setCurrentItem(newCalculation)
  calculationStore.openCreateUpdateDialog()
}

const saveCalculation = async (item: CalculationFormPayload) => {
  const response = await calculationStore.saveCalculationForm(item)
  if (response) {
    calculationStore.closeCreateUpdateDialog()
    const queuedCalculation = response.input_auto_start
      ? response
      : await calculationStore.celeryCalculate(response)
    const calculation = queuedCalculation ?? response
    if (currentProjectItem.value?.id) {
      void projectStore.fetchSpending(currentProjectItem.value.id)
    }
    await router.push({
      name: 'calculation-result',
      params: {
        id: calculation.project,
        calculationId: calculation.id
      }
    })
  }
}

const updateSpendingCap = async (value: ProjectSpendingCapPayload) => {
  if (!currentProjectItem.value?.id) return
  await projectStore.updateSpendingCap(currentProjectItem.value.id, value)
}

watch(deleteDialog, () => {
  if (!deleteDialog.value) {
    calculationStore.setDeleteItem(null)
  }
})

watch(projectCreateUpdateDialog, (isOpen) => {
  if (!isOpen) projectStore.clearProjectFormErrors()
})

watch(
  () => currentProjectItem.value?.id,
  (projectId) => {
    if (projectId) {
      void projectStore.fetchSpending(projectId)
    }
  },
  { immediate: true }
)
</script>

<template>
  <template v-if="calculationFetchLoading">
    <div class="d-flex justify-center align-center fill-height">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>
  </template>
  <template v-else>
    <div class="px-8 py-6">
      <div v-if="currentProjectItem" class="project-heading mb-6">
        <h1 class="project-title">{{ currentProjectItem.name }}</h1>
        <v-tooltip v-if="canChangeProject" text="Edit project" location="top">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              color="primary"
              density="comfortable"
              icon="mdi-pencil"
              variant="text"
              @click="handleEditProject"
            />
          </template>
        </v-tooltip>
      </div>

      <template v-if="currentProjectItem">
        <h2 class="section-title mb-1">Spending</h2>
        <p class="section-description mb-3">
          Spending data is updated every 5 minutes. Active calculations are automatically cancelled
          if either estimate exceeds its cap.
        </p>
      </template>
      <ProjectSpendingSummary
        v-if="currentProjectItem"
        :project="currentProjectItem"
        :spending="spending"
        :loading="spendingLoading"
        :write-loading="spendingWriteLoading"
        @update-cap="updateSpendingCap"
      />

      <v-card class="mt-6" border="opacity-25 sm" elevation="0" flat>
        <v-toolbar color="transparent">
          <v-toolbar-title>Calculations</v-toolbar-title>
          <v-spacer />
          <v-text-field
            v-model="search"
            class="calculation-search mr-3"
            density="compact"
            hide-details
            label="Search calculations"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
          />
          <v-btn
            v-if="canAddCalculation"
            class="mr-3 text-none"
            color="primary"
            prepend-icon="mdi-plus"
            variant="flat"
            @click="addCalculation"
          >
            Add Calculation
          </v-btn>
        </v-toolbar>
        <v-data-table
          :headers="headers"
          :items="filteredCalculations"
          :items-per-page="50"
          item-value="id"
          hover
          @click:row="navigateToCalculation"
        >
          <template v-slot:[`item.status`]="{ item }">{{ getStatus(item) }}</template>
          <template v-slot:[`item.hardware_profile_name`]="{ item }">
            {{ item.hardware_profile_name || '-' }}
          </template>
          <template v-slot:[`item.zip_file_name`]="{ item }">
            <button
              v-if="item.zip_file_name"
              class="zip-download-link"
              type="button"
              :disabled="zipFileDownloadLoading === item.id"
              @click.stop="downloadZipFile(item)"
            >
              {{ item.zip_file_name }}
            </button>
            <span v-else>-</span>
          </template>
          <template v-slot:[`item.estimated_monthly_storage_cost`]="{ item }">
            {{ formatMoney(item, item.estimated_monthly_storage_cost, '/mo') }}
          </template>
          <template v-slot:[`item.estimated_compute_cost`]="{ item }">
            {{ formatMoney(item, item.estimated_compute_cost) }}
          </template>
          <template v-slot:[`item.created`]="{ item }">{{ formatDate(item.created) }}</template>
          <template v-slot:[`item.vm_runtime_deadline`]="{ item }">
            {{ formatDate(item.vm_runtime_deadline) }}
          </template>
          <template v-slot:[`item.ended`]="{ item }">{{ formatDate(item.ended) }}</template>
          <template v-slot:[`item.duration`]="{ item }">{{ formatDuration(item) }}</template>
          <template v-slot:[`item.actions`]="{ item }">
            <CalculationTableActions
              :item="item"
              :spending-cap-exceeded="Boolean(spending?.limit_exceeded)"
              :spending-loaded="spendingLoaded"
              :spending-loading="spendingLoading"
            />
          </template>
          <template #no-data>
            <div class="py-8 text-medium-emphasis">No calculations found.</div>
          </template>
        </v-data-table>
      </v-card>

      <ProjectCreateUpdateDialog
        v-if="currentProjectItem"
        v-model="projectCreateUpdateDialog"
        :item="currentProjectItem"
        :loading="projectWriteLoading"
        :fields="projectDialogFields"
        :work-order-field="projectWorkOrderField"
        :disabled-fields="projectDialogDisabledFields"
        :errors="projectFormErrors"
        @clear-error="projectStore.clearProjectFormErrors"
        @submit="saveProject"
      />
      <CalculationCreateUpdateDialog
        v-if="currentCalculationItem"
        v-model="calculationCreateUpdateDialog"
        :item="currentCalculationItem"
        :loading="calculationWriteLoading"
        :spending="spending"
        @submit="saveCalculation"
      />
      <DeleteDialog
        v-if="deleteItem"
        v-model="deleteDialog"
        :loading="deleteLoading"
        :name="deleteItem?.name"
        :item="deleteItem"
        @set-delete-item="deleteItem = $event"
        @delete="calculationStore.delete(deleteItem)"
      />
    </div>
  </template>
</template>

<style scoped>
.project-heading {
  align-items: center;
  display: flex;
  gap: 8px;
}

.project-title {
  font-size: 28px;
  font-weight: 500;
  line-height: 1.2;
  margin: 0;
}

.section-title {
  font-size: 24px;
  font-weight: 500;
  line-height: 1.3;
}

.section-description {
  color: rgba(var(--v-theme-on-surface), 0.7);
  font-size: 14px;
  line-height: 1.4;
}

.calculation-search {
  max-width: 280px;
}

.zip-download-link {
  background: none;
  border: 0;
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-align: left;
  text-decoration: underline;
}

.zip-download-link:disabled {
  cursor: progress;
  opacity: 0.7;
}

:deep(.v-data-table tbody tr) {
  cursor: pointer;
}
</style>
